import { ImageOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { backLabelFor } from '../properties/property-shared/propertyEntry';

interface Props {
  /** The workspace's Back (it carries the Global Orders tab). */
  onBack: () => void;
}

/**
 * The internal workspace's answer when the photoset its route names is not
 * one of the property's (DV-2849 PR 3, spec §4.6): a dangling order link, a
 * set on another property, or a stale copied link. Same shape as
 * PropertyNotFound / PropertyLoadError beside it.
 *
 * Offers Back only. Listing the property's other photosets here would be the
 * substitution the ticket's unhappy path forbids. Nothing in the app can
 * relink the order either: its Global Orders row has a target, so it shows
 * Manage Assets (which leads back here), not Link Photoset, and asset-service
 * 409s a relink of an order that already has a link. So the copy asks for the
 * page's link to go to engineering (spec §4.6, 2026-09-29 PR-review correction).
 */
export function PhotosetNotFound({ onBack }: Props) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <ImageOff className="h-12 w-12 mx-auto text-muted-foreground" aria-hidden="true" />
        <Typography as="h1" variant="heading-lg" className="font-semibold">
          No Photoset for This Order
        </Typography>
        <Typography className="text-muted-foreground">
          The photoset this order points to couldn&apos;t be found, so there&apos;s nothing to
          show. Send this page&apos;s link to engineering so the order&apos;s photoset link can be
          fixed.
        </Typography>
        <div className="flex items-center justify-center pt-2">
          <Button variant="outline" onClick={onBack}>
            {backLabelFor('global-orders')}
          </Button>
        </div>
      </div>
    </div>
  );
}
